import * as React from "react";
import ReactDOMServer from "react-dom/server";
// import { IndexByString } from '../../../redux/modules/insights/utils'
import {
  Zone,
  ViewBy,
  RankDir,
  Zones,
} from "../../../context/reports/comfort/types";
import { orderZones } from "../../../context/reports/comfort/utils";
import { ToolTip } from "./components/ToolTip";
import {
  combineZones,
  calculateYAxisMaxValue,
  createLegendItemsUsingDates,
} from "./utils";
import { scrollBar2DChartBaseConfig } from "../ChartBaseConfigs";

type ChartDataObject = {
  [key: string]: any;
};

const CHART_SETTINGS = {
  yAxisName:
    "Total hours uncomfortable during occupancy (zones more than 2℃ outside of set point)",
  canvasBgColor: "#FFF5F5", // palette.red1
  plotHoverEffect: 0,
  canvasBgAlpha: 100,
  maxLabelWidthPercent: 15,
  stack100Percent: false,
  reverseLegend: true,
  theme: "fusion",
  ...scrollBar2DChartBaseConfig,
};
const CHART_CONFIG = {
  type: "scrollbar2d",
  width: "100%",
  height: "500",
};

function zonesByPct(
  rankDir: RankDir,
  viewBy: ViewBy,
  zones: Zones,
  startDate: string,
  endDate: string
) {
  const data = orderZones(rankDir, viewBy, Object.values(zones.current.zones));
  const yAxisMaxValue = 100;

  const formattedData = data.reduce(
    (acc: ChartDataObject, d) => {
      if (d.pctDiscomfort) {
        acc.categories.category.push({
          label: d.name,
        });

        acc.dataset.uncomfortable.push({
          value: d.pctDiscomfort,
          valueFontBold: true,
          displayValue: `${Math.round(d.pctDiscomfort)}%`,
          showValue: true,
          toolText: ReactDOMServer.renderToStaticMarkup(
            <ToolTip
              name={d.name}
              categoryOccupiedHours={d.hoursDiscomfort}
              categoryOccupiedPct={d.pctDiscomfort}
              totalOccupiedHours={d.hoursOccupied}
              category="uncomfortable"
              from={startDate}
              to={endDate}
            />
          ),
        });

        acc.dataset.comfortable.push({
          value: d.pctComfort,
          showValue: false,
          toolText: ReactDOMServer.renderToStaticMarkup(
            <ToolTip
              name={d.name}
              categoryOccupiedHours={d.hoursComfort}
              categoryOccupiedPct={d.pctComfort}
              totalOccupiedHours={d.hoursOccupied}
              category="comfortable"
              from={startDate}
              to={endDate}
            />
          ),
        });
      }

      return acc;
    },
    {
      categories: {
        category: [],
      },
      dataset: {
        comfortable: [],
        uncomfortable: [],
      },
    }
  );

  const dataSource = {
    chart: {
      ...CHART_SETTINGS,
      showValues: true,
      yAxisMaxValue,
    },
    categories: [
      {
        category: formattedData.categories.category,
      },
    ],
    dataset: [
      {
        seriesname: "% of occupied hours uncomfortable",
        data: formattedData.dataset.uncomfortable,
        showValues: false,
        color: "#db2b26",
      },
    ],
  };

  return {
    ...CHART_CONFIG,
    dataSource,
  };
}

function zonesByHours(
  rankDir: RankDir,
  viewBy: ViewBy,
  zones: Zones,
  startDate: string,
  endDate: string
) {
  let yAxisMaxValue = calculateYAxisMaxValue(zones.current, zones.compare);

  // After the initial data proccessing, current object could be null but
  // compare could exist and vice versa. But it's very likely that both
  // current and compare would exist
  //
  // This means that when we handle the data proccessing in the next
  // step, it will depend on these object existence
  const combinedData: Array<{
    current: Zone | null;
    compare: Zone | null;
  }> = combineZones(rankDir, viewBy, zones.current, zones.compare);

  function whoExists(
    current: Zone | null,
    compare: Zone | null
  ): "CURRENT" | "COMPARE" | "BOTH" {
    if (current && !compare) return "CURRENT";
    else if (compare && !current) return "COMPARE";

    return "BOTH";
  }

  const formattedData = combinedData.reduce(
    (acc: ChartDataObject, d) => {
      const current = d.current;
      const compare = d.compare;

      const existResult = whoExists(current, compare);

      switch (existResult) {
        case "CURRENT": {
          acc.categories.category.push({
            label: current!.name,
          });

          acc.dataset.current.uncomfortable.push({
            value: current!.hoursDiscomfort,
            displayValue: `${Math.round(current!.hoursDiscomfort)} h`,
            // * Hide the value if there isn't enough space to display it in bar
            showValue: true,
            toolText: ReactDOMServer.renderToStaticMarkup(
              <ToolTip
                name={current!.name}
                categoryOccupiedHours={current!.hoursDiscomfort}
                categoryOccupiedPct={current!.pctDiscomfort}
                totalOccupiedHours={current!.hoursOccupied}
                category="uncomfortable"
                from={startDate}
                to={endDate}
              />
            ),
          });
          acc.dataset.compare.uncomfortable.push({
            value: null,
            displayValue: `No data`,
            showValue: true,
            toolText: ReactDOMServer.renderToStaticMarkup(
              <p>No data to display</p>
            ),
          });
          break;
        }

        case "COMPARE": {
          acc.categories.category.push({
            label: compare!.name,
          });

          acc.dataset.compare.uncomfortable.push({
            value: compare!.hoursDiscomfort,
            displayValue: `${Math.round(compare!.hoursDiscomfort)} h`,
            // * Hide the value if there isn't enough space to display it in bar
            showValue: true,
            toolText: ReactDOMServer.renderToStaticMarkup(
              <ToolTip
                name={compare!.name}
                categoryOccupiedHours={compare!.hoursDiscomfort}
                categoryOccupiedPct={compare!.pctDiscomfort}
                totalOccupiedHours={compare!.hoursOccupied}
                category="uncomfortable"
                from={zones.compare!.from}
                to={zones.compare!.to}
              />
            ),
          });

          acc.dataset.current.uncomfortable.push({
            value: null,
            displayValue: `No data`,
            showValue: true,
            toolText: ReactDOMServer.renderToStaticMarkup(
              <p>No data to display</p>
            ),
          });
          break;
        }

        case "BOTH": {
          acc.categories.category.push({
            label: current!.name,
          });

          acc.dataset.current.uncomfortable.push({
            value: current!.hoursDiscomfort,
            displayValue: `${Math.round(current!.hoursDiscomfort)} h`,
            // * Hide the value if there isn't enough space to display it in bar
            showValue: true,
            toolText: ReactDOMServer.renderToStaticMarkup(
              <ToolTip
                name={current!.name}
                categoryOccupiedHours={current!.hoursDiscomfort}
                categoryOccupiedPct={current!.pctDiscomfort}
                totalOccupiedHours={current!.hoursOccupied}
                category="uncomfortable"
                from={startDate}
                to={endDate}
              />
            ),
          });
          acc.dataset.compare.uncomfortable.push({
            value: compare!.hoursDiscomfort,
            displayValue: `${Math.round(compare!.hoursDiscomfort)} h`,
            // * Hide the value if there isn't enough space to display it in bar
            showValue: true,
            toolText: ReactDOMServer.renderToStaticMarkup(
              <ToolTip
                name={compare!.name}
                categoryOccupiedHours={compare!.hoursDiscomfort}
                categoryOccupiedPct={compare!.pctDiscomfort}
                totalOccupiedHours={compare!.hoursOccupied}
                category="uncomfortable"
                from={zones.compare!.from}
                to={zones.compare!.to}
              />
            ),
          });

          break;
        }

        default:
          break;
      }

      return acc;
    },
    {
      categories: {
        category: [],
      },
      dataset: {
        current: {
          uncomfortable: [],
        },
        compare: {
          uncomfortable: [],
        },
        // comfortable: [],
        // cold: [],
        // hot: []
      },
    }
  );

  const dataSource = {
    chart: {
      ...CHART_SETTINGS,
      showValues: true,
      yAxisMaxValue,
    },
    categories: [
      {
        category: formattedData.categories.category,
      },
    ],
    dataset: [
      {
        seriesname: createLegendItemsUsingDates(
          zones.current.from,
          zones.current.to
        ),
        data: formattedData.dataset.current.uncomfortable,
        valueFontBold: true,
        color: "#db2b26",
      },
      {
        seriesname: zones.compare
          ? createLegendItemsUsingDates(zones.compare.from, zones.compare.to)
          : "",
        data: formattedData.dataset.compare.uncomfortable,
        valueFontBold: true,
        color: "#ff9999",
      },
    ],
  };

  return {
    ...CHART_CONFIG,
    dataSource,
  };
}

export function getZonesChartConfig(
  viewBy: ViewBy,
  rankDir: RankDir,
  startDate: string,
  endDate: string,
  zones?: Zones
) {
  if (!zones) return;

  switch (viewBy) {
    case "pctDiscomfort":
      return zonesByPct(rankDir, "pctDiscomfort", zones, startDate, endDate);
    case "hoursDiscomfort":
      return zonesByHours(
        rankDir,
        "hoursDiscomfort",
        zones,
        startDate,
        endDate
      );
    default:
      return zonesByPct(rankDir, "pctComfort", zones, startDate, endDate);
  }
}
